import { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { preCreateOrder } from "@/api/car";

import { ProductType, InsuranceId, type Traveler } from "./types";
import { travelerFromApi, travelersToConfirmApi } from "./travelerMap";
import { productInfoMap, travelInsurancePlans, CAR_DAYS } from "./constants";
import { fetchCarOrderDetail, confirmOrder } from "@/api/car";
import {
  getHotelOrderDetail,
  confirmHotelOrder,
  getRoomDetail,
  type OrderDetailData as HotelOrderDetailData,
  type HotelConfirmTraveler,
  type RoomDetail,
} from "@/api/hotel";
import { toastWarning } from "@/utils/toast";
import { fetchUserTravelerList } from "@/api/user";

import { OrderHeader } from "./components/OrderHeader";
import { TripSummary } from "./components/TripSummary";
import { ProductCard } from "./components/ProductCard";
import { ContactInfo } from "./components/ContactInfo";
import { CarLicenseWarning } from "./components/CarLicenseWarning";
import { TravelersList } from "./components/TravelersList";
import { SpecialRequest } from "./components/SpecialRequest";
import { OrderFooter } from "./components/OrderFooter";

/** 联系人信息表单 */
interface ContactForm {
  name: string;
  phone: string;
  email: string;
}

/**
 * 统一订单详情展示数据（兼容租车 + 酒店两种类型）
 * 将不同业务线的原始字段映射到 UI 组件所需的通用字段
 */
interface UnifiedOrderDetail {
  order_no: string;
  total_amount: number;

  /* ===== TripSummary 所需 ===== */
  /** 取车/入住 地点 */
  pickupLocation?: string;
  /** 还车/离店 地点 */
  returnLocation?: string;
  /** 取车/入住 日期 */
  pickupDate?: string;
  /** 还车/离店 日期 */
  returnDate?: string;
  /** 天数/晚数 */
  rentDays?: number;

  /* ===== ProductCard 所需 ===== */
  /** 产品名称（车辆名 / 房型） */
  carTitle?: string;
  /** 封面图片（酒店房型图 / 车辆图） */
  coverImage?: string;
  /** 产品金额（车辆费 / 房间费） */
  carAmount?: string;
  /** 单价（日租金 / 每晚价格） */
  dailyPrice?: string;

  /* ===== 其他 ===== */
  contact?: { name: string; phone: string; email?: string };
  travelers?: any[];
  special_requests?: string[];
  insuranceTypeText?: string;
}

export default function OrderFormPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const routeState = location.state as {
    type?: string;
    orderId?: number;
    orderNo?: string;
    roomId?: number;
  };

  const singleType = routeState?.type || "hotel";
  const orderId = routeState?.orderId;
  const roomId = routeState?.roomId;

  console.log(
    "[order-form] routeState:",
    routeState,
    "orderId:",
    orderId,
    "roomId:",
    roomId,
  );

  // 联系人信息
  const [contact, setContact] = useState<ContactForm>({
    name: "",
    phone: "",
    email: "",
  });

  // 订单详情（统一类型，兼容租车和酒店）
  const [orderDetail, setOrderDetail] = useState<UnifiedOrderDetail | null>(
    null,
  );
  const [loading, setLoading] = useState(false);

  const [insurance, setInsurance] = useState<InsuranceId>("none");
  const [carExtraInsurance, setCarExtraInsurance] = useState(false);
  const [travelers, setTravelers] = useState<Traveler[]>([]);
  const travelersCount = travelers.length;

  // 进入页面时获取订单详情/房型详情，并回填数据
  useEffect(() => {
    // ── 登录回跳：sessionStorage 中有待处理的下单数据 ─────────────
    if (!orderId) {
      const raw = sessionStorage.getItem("pending_car_booking");
      if (raw) {
        try {
          const { carId, tripInfo } = JSON.parse(raw);
          sessionStorage.removeItem("pending_car_booking");
          // 预创建订单后，用 navigate 重新进入本页面（携带 orderId）
          preCreateOrder({
            car_id: carId,
            pickup_date: tripInfo.pickupTime.split(" ")[0],
            pickup_time: tripInfo.pickupTime.split(" ")[1] || "10:00",
            return_date: tripInfo.returnTime.split(" ")[0],
            return_time: tripInfo.returnTime.split(" ")[1] || "10:00",
            pickup_location: tripInfo.pickupLocation,
            return_location: tripInfo.returnLocation,
          })
            .then((result) => {
              navigate("/order-form", {
                state: { type: "car", orderId: result.order_id },
                replace: true,
              });
            })
            .catch((err) => console.error("预创建订单失败（登录回跳）:", err));
          return;
        } catch {
          sessionStorage.removeItem("pending_car_booking");
        }
      }
    }

    // 有 orderId → 获取已有订单详情（编辑/再次预订回填）
    if (orderId) {
      setLoading(true);
      const fetchDetail = async () => {
        try {
          if (singleType === "hotel") {
            // 酒店订单详情 → 映射为统一字段
            const res = await getHotelOrderDetail(orderId);
            if (res.data.code === 200 && res.data.data) {
              const d: HotelOrderDetailData = res.data.data;
              const baseCover = d.room?.cover || d.hotel?.cover || "";
              setOrderDetail({
                order_no: d.order_no,
                total_amount: d.total_amount,
                // TripSummary: 酒店 → 入住/离店地址和日期
                pickupLocation: d.hotel?.address,
                returnLocation: d.hotel?.address,
                pickupDate: d.check_in_date,
                returnDate: d.check_out_date,
                rentDays: d.nights,
                // ProductCard: 酒店名称 + 房型
                carTitle: d.room?.name
                  ? `${d.hotel?.name} - ${d.room?.name}`
                  : d.hotel?.name,
                coverImage: baseCover,
                carAmount: String(d.total_amount),
                dailyPrice: d.room_price ? String(d.room_price) : undefined,
                // 其他
                special_requests: d.special_requests,
              });
              // 回显联系人
              if (d.guest_name || d.guest_phone) {
                setContact({
                  name: d.guest_name || "",
                  phone: d.guest_phone || "",
                  email: "",
                });
              }
              // 订单封面为空且带 roomId 时，用房型详情接口补一张真实封面图
              if (!baseCover && roomId) {
                getRoomDetail(roomId)
                  .then((rr) => {
                    if (
                      rr.data.code === 200 &&
                      rr.data.data &&
                      (rr.data.data.cover_image ||
                        rr.data.data.images?.[0])
                    ) {
                      setOrderDetail((prev) =>
                        prev
                          ? {
                              ...prev,
                              coverImage:
                                rr.data.data.cover_image ||
                                rr.data.data.images?.[0],
                            }
                          : prev,
                      );
                    }
                  })
                  .catch((err) =>
                    console.error("补充房型封面图失败:", err),
                  );
              }
            }
          } else {
            // 租车订单详情 → 映射为统一字段
            const data = await fetchCarOrderDetail(orderId);
            setOrderDetail({
              order_no: data.order_no,
              total_amount: Number(data.fee?.total_amount ?? 0),
              // TripSummary
              pickupLocation: data.pickup?.location ?? undefined,
              returnLocation: data.return?.location ?? undefined,
              pickupDate: data.pickup?.date ?? "",
              returnDate: data.return?.date ?? "",
              rentDays: data.rent_days,
              // ProductCard
              carTitle: data.car?.title,
              carAmount: data.fee?.car_amount,
              dailyPrice: data.fee?.daily_price,
              coverImage: data.car?.cover,
              // 其他
              contact: data.contact,
              travelers: data.travelers,
              special_requests: data.special_requests,
              insuranceTypeText: data.insurance?.type_text,
            });
            if (data.contact && (data.contact.name || data.contact.phone)) {
              const c: ContactForm = {
                name: data.contact.name || "",
                phone: data.contact.phone || "",
                email: "",
              };
              setContact(c);
            }
            if (data.travelers?.length) {
              setTravelers(data.travelers.map(travelerFromApi));
            }
          }
        } catch (err) {
          console.error("获取订单详情失败:", err);
        } finally {
          setLoading(false);
        }
      };
      fetchDetail();
      return;
    }

    // 无 orderId 但有 roomId（再次预订）→ 调用房型详情 API 获取真实数据
    if (roomId && singleType === "hotel") {
      setLoading(true);
      getRoomDetail(roomId)
        .then((res) => {
          if (res.data.code === 200 && res.data.data) {
            const d: RoomDetail = res.data.data;
            setOrderDetail({
              order_no: "",
              total_amount: d.price,
              pickupLocation: d.hotel_name || "",
              returnLocation: d.hotel_name || "",
              pickupDate: "",
              returnDate: "",
              rentDays: 1,
              carTitle: `${d.hotel_name} - ${d.room_name}`,
              coverImage: d.cover_image,
              carAmount: String(d.price),
              dailyPrice: String(d.price),
              special_requests: [],
            });
          }
        })
        .catch((err) => console.error("获取房型详情失败:", err))
        .finally(() => setLoading(false));
      return;
    }
  }, [orderId, roomId, singleType]);

  // ── 进入页面即获取用户出行人库列表（回填常用出行人）────────────
  useEffect(() => {
    fetchUserTravelerList(1, 20)
      .then((list) => {
        if (list.length > 0 && travelers.length === 0) {
          const mapped = list.map((item) => ({
            name: item.name,
            type: "成人" as string,
            idCard: item.card_no || "",
            phone: item.phone || undefined,
            idCardFront: item.id_card_front ?? undefined,
            idCardBack: item.id_card_back ?? undefined,
            travelerId: item.id,
            isDefault: item.is_default === 1,
          }));
          setTravelers(mapped);
        }
      })
      .catch((err) => console.error("获取出行人列表失败:", err));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // 产品信息
  const productInfo = productInfoMap[singleType as ProductType];
  // 金额优先从订单详情获取
  const baseTotal = orderDetail?.total_amount
    ? Number(orderDetail.total_amount)
    : productInfo?.price || 0;

  // 保险费用
  const insurancePrice =
    travelInsurancePlans.find((p) => p.id === insurance)?.price ?? 0;
  const insuranceCost =
    singleType === "car"
      ? carExtraInsurance
        ? 80 * CAR_DAYS
        : 0
      : insurancePrice * travelersCount;

  const total = baseTotal + insuranceCost;

  const driverTraveler = travelers.find((t) => t.isDriver);

  // 确认订单 → 去支付
  const handleConfirmAndPay = async () => {
    if (!orderDetail?.order_no) {
      toastWarning("订单信息加载中，请稍候");
      return;
    }
    if (loading) return;

    if (
      !contact.name.trim() ||
      !contact.phone.trim() ||
      !contact.email.trim()
    ) {
      toastWarning("请填写完整的联系人信息（邮箱必填，用于接收凭证）");
      return;
    }
    if (travelers.length === 0) {
      toastWarning("请添加出行人员");
      return;
    }

    setLoading(true);
    try {
      if (singleType === "hotel") {
        const hotelTravelers: HotelConfirmTraveler[] = travelers.map((t) => ({
          name: t.name,
          type_name: t.type,
          id_card: t.idCard,
          phone: t.phone,
          id_card_front: t.idCardFront,
          id_card_back: t.idCardBack,
        }));
        await confirmHotelOrder({
          order_no: orderDetail.order_no,
          guest_name: contact.name,
          guest_phone: contact.phone,
          guest_email: contact.email || "",
          travelers: hotelTravelers,
          special_requests: undefined,
        });
      } else {
        await confirmOrder({
          order_no: orderDetail.order_no,
          contact_name: contact.name,
          contact_phone: contact.phone,
          contact_email: contact.email || undefined,
          travelers:
            travelers.length > 0 ? travelersToConfirmApi(travelers) : undefined,
        });
      }
      navigate("/payment", {
        state: {
          orderNo: orderDetail.order_no,
          totalAmount: String(total),
          orderId,
          type: singleType,
        },
      });
    } catch (err) {
      console.error("确认订单失败:", err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="min-h-screen pb-[calc(env(safe-area-inset-bottom,0px)+144px)]"
      style={{
        background: "#F7F7F7",
        fontFamily: "'SF Pro Text','PingFang SC','Inter',system-ui,sans-serif",
      }}
    >
      <OrderHeader />
      <div className="px-4 py-4 space-y-3">
        <TripSummary
          spec={productInfo?.spec || ""}
          pickupLocation={orderDetail?.pickupLocation}
          returnLocation={orderDetail?.returnLocation}
          pickupDate={orderDetail?.pickupDate}
          returnDate={orderDetail?.returnDate}
          rentDays={orderDetail?.rentDays}
        />
        <ProductCard
          productInfo={productInfo!}
          productType={singleType as ProductType}
          basePrice={baseTotal}
          carAmount={orderDetail?.carAmount}
          dailyPrice={orderDetail?.dailyPrice}
          carTitle={orderDetail?.carTitle}
          coverImage={orderDetail?.coverImage}
          pickupLocation={orderDetail?.pickupLocation}
          rentDays={orderDetail?.rentDays}
          insuranceTypeText={orderDetail?.insuranceTypeText}
          insurance={insurance}
          onInsuranceChange={setInsurance as any}
          carExtraUpgrade={carExtraInsurance}
          onCarExtraToggle={() => setCarExtraInsurance(!carExtraInsurance)}
        />

        <ContactInfo
          contact={contact}
          defaultContact={orderDetail?.contact}
          onChange={setContact}
        />

        {singleType === "car" && <CarLicenseWarning driver={driverTraveler} />}

        <TravelersList
          productType={singleType as ProductType}
          travelers={travelers}
          onTravelersChange={setTravelers}
        />

        <SpecialRequest
          defaultValue={orderDetail?.special_requests?.join("；")}
        />
      </div>

      <OrderFooter
        baseTotal={baseTotal}
        insuranceCost={insuranceCost}
        total={total}
        loading={loading}
        onSubmit={handleConfirmAndPay}
      />
    </div>
  );
}
